import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { api } from '../../services/api';

interface OrderDialogProps {
  open: boolean;
  customerId: number | null;
  customers: { id: number; first_name: string; last_name: string; phone?: string }[];
  onClose: () => void;
  onSuccess: () => void;
}

export default function OrderDialog({ open, customerId, customers, onClose, onSuccess }: OrderDialogProps) {
  const [form, setForm] = useState({
    customer_id: 0,
    description: '',
    priority: 'normal',
    estimated_price: '',
  });

  useEffect(() => {
    if (open) {
      setForm({
        customer_id: customerId || 0,
        description: '',
        priority: 'normal',
        estimated_price: '',
      });
    }
  }, [open, customerId]);

  const handleSubmit = async () => {
    try {
      await api.post('/orders', {
        ...form,
        estimated_price: form.estimated_price ? parseFloat(form.estimated_price) : null,
      });
      onSuccess();
    } catch (error) {
      console.error('Error creating order:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Crear Nuevo Pedido</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Cliente</InputLabel>
            <Select
              value={form.customer_id}
              onChange={(e) => setForm({ ...form, customer_id: Number(e.target.value) })}
              label="Cliente"
            >
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.last_name} {customer.first_name} - {customer.phone}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Descripcion del Pedido"
            multiline
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            sx={{ mb: 2 }}
            placeholder="Ej: Pasame cotizacion de un televisor Samsung 55 pulgadas"
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Prioridad</InputLabel>
            <Select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              label="Prioridad"
            >
              <MenuItem value="low">Baja</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="high">Alta</MenuItem>
              <MenuItem value="urgent">Urgente</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Precio Estimado"
            type="number"
            value={form.estimated_price}
            onChange={(e) => setForm({ ...form, estimated_price: e.target.value })}
            inputProps={{ min: 0, step: 0.01 }}
            helperText="Opcional - Precio estimado si se conoce"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!form.customer_id || !form.description.trim()}
        >
          Crear Pedido
        </Button>
      </DialogActions>
    </Dialog>
  );
}
